"use client";

import Image from "./image";
import { motion } from "motion/react";
import { useState } from "react";
import {
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
} from "./core/disclosure";

type CaseItem = {
  category: string;
  title: string;
  subtitle: string;
  image: string;
  deliverables: string[];
  facts: Array<{ label: string; value: string }>;
};

const imageVariants = {
  collapsed: { scale: 1, filter: "brightness(1) saturate(.85)" },
  expanded: { scale: 1.06, filter: "brightness(.58) saturate(.65)" },
};

const transition = {
  type: "spring" as const,
  stiffness: 26.7,
  damping: 4.1,
  mass: 0.2,
};

const contentVariants = {
  collapsed: { opacity: 0, y: 0 },
  expanded: { opacity: 1, y: 0 },
};

export function CaseDisclosureCard({ item }: { item: CaseItem }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`case-disclosure-card ${isOpen ? "is-open" : ""}`}>
      <button
        aria-expanded={isOpen}
        aria-label={`${isOpen ? "收起" : "查看"}${item.title}`}
        className="case-disclosure-visual-trigger"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <motion.div
          animate={isOpen ? "expanded" : "collapsed"}
          className="case-disclosure-image"
          transition={transition}
          variants={imageVariants}
        >
          <Image
            src={item.image}
            alt=""
            fill
            sizes="(max-width: 900px) 100vw, 33vw"
          />
        </motion.div>
        <span className="case-category">{item.category}</span>
      </button>

      <Disclosure
        className="case-disclosure-panel"
        onOpenChange={setIsOpen}
        open={isOpen}
        transition={transition}
        variants={contentVariants}
      >
        <DisclosureTrigger>
          <button className="case-disclosure-trigger" type="button">
            <span>{item.title}</span>
            <span aria-hidden="true">{isOpen ? "−" : "+"}</span>
          </button>
        </DisclosureTrigger>
        <DisclosureContent className="case-disclosure-content">
          <p>{item.subtitle}</p>
          <div className="case-deliverables">
            {item.deliverables.map((value) => (
              <span key={value}>{value}</span>
            ))}
          </div>
          <div className="case-status">
            {item.facts.map(({ label, value }) => (
              <div key={label}>
                <small>{label}</small>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </DisclosureContent>
      </Disclosure>
    </div>
  );
}
